<template>
    <div class="program-component">
        <card title="Insurance Plan Record">
            <b-alert v-if="parsedPlanData && hasAppliedParsedData" show variant="info" class="mb-3">
                <i class="fas fa-robot"></i> AI parsed insurance data has been pre-filled. Please review all fields and make any necessary corrections.
            </b-alert>
            <form>
                <b-row>
                    <b-col>
                        <SearchComponent v-if="!newMode" :disabled="!!(plan.id)" v-model="plan" :search-u-r-l="`/Plan`"
                            :id="plan && plan.id" @input="onPlanChanged"></SearchComponent>

                    </b-col>
                </b-row>
                <b-form-row>
                    <b-col>
                        <fg-input v-model="plan.name" label='Name' :disabled="disableFields" required></fg-input>
                    </b-col>
                    <b-col cols="2">
                        <fg-input v-model="plan.bin" label='BIN' @blur="binSet" max-length="6" required
                            :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="2">
                        <fg-input v-model="plan.pcn" label='PCN' max-length="10" :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="2">
                        <EnumSelector v-model="plan.providerType" label='Provider Type' :enum-codes="providerTypeEnums"
                            :disabled="disableFields"></EnumSelector>
                    </b-col>
                    <b-col cols="2">
                        <!--                    <fg-input v-model="plan.vendorId" label="Vendor Id" max-length="50"-->
                        <!--                              :disabled="disableFields"></fg-input>-->
                    </b-col>
                    <b-col cols="12">
                        <fg-input v-model="plan.address1" label='Address 1' :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="12">
                        <fg-input v-model="plan.address2" label='Address 2' :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model="plan.addressCity" label='City' :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model="plan.addressState" label='State' :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model="plan.addressZip" label='Zip' :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model="plan.phoneNumber" label='PhoneNumber' :disabled="disableFields">
                        </fg-input>
                    </b-col>

                    <!-- Default values-->
                    <b-col cols="3">
                        <fg-input v-model="plan.defaultGroupId" label='Default GroupId' :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model="plan.defaultCardholderId" label='Default CardholderId'
                            :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model="plan.defaultPersonCode" label='Default Person Code'
                            :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col cols="3">
                        <EnumSelector label="Default Relationship Code" v-model="plan.defaultRelationshipCode"
                            :enumCodes="relationshipCodes" :disabled="disableFields" />
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col cols="3">
                        <fg-input label="Audit Percentage" v-model.number="orderAuditConfig.percentageChance"
                            type="number" :disabled="disableFields" />
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col cols="3">
                        <search-component v-model="pbm" search-u-r-l="/PharmacyBenefitManager" label="PBM" clearable
                            prepopulate :disabled="disableFields" />
                    </b-col>
                </b-form-row>
                <b-form-group label="Message" :disabled="disableFields">
                    <b-form-textarea v-model="plan.planMessage"></b-form-textarea>
                </b-form-group>
                <b-form-row>
                    <b-col cols="3">
                        <checkbox v-model="isOffline" :disabled="disableFields" block>
                            Is an Offline Plan?
                            <fg-input v-model.number="plan.offlineCopay" v-if="isOffline" label="Copay Amount"
                                type="number" :disabled="disableFields" required>
                            </fg-input>
                        </checkbox>
                    </b-col>
                </b-form-row>
                <br>
                <b-form-row>
                    <b-col>
                        <b-form-group label="Billing Flags" :disabled="disableFields">
                            <b-form-checkbox-group id="checkbox-group-1" v-model="plan.selectedFlags"
                                :options="flagOptions"></b-form-checkbox-group>
                        </b-form-group>
                    </b-col>
                </b-form-row>

            </form>

            <b-row v-if="plan.id">
                <b-col>
                    <h5>Notes</h5>
                    <NotesForm :entity-id="plan.id" :entityType="EntityType.Plan" save-button-label="Add Note"
                        text-box-label="Note" @saved="refreshNotes" />
                    <NotesList ref="notesList" :entity-id="plan.id" :entity-type="EntityType.Plan" :page-size="5" />
                </b-col>
            </b-row>

            <hr>
            <!-- Inventory CSV File -->
            <div v-if="!disableFields">
                <h3>Codes Inventory Upload</h3>
                <b-row v-if="plan && plan.id">
                    <b-col cols="12" v-if="plan.inventoryQuantityAvailable">
                        <b>Codes Available: </b> {{ plan.inventoryQuantityAvailable }}
                    </b-col>
                    <b-col cols="12" v-if="plan.inventoryLastUpdate">
                        <b>Latest uploaded available code at: </b>
                        {{ plan.inventoryLastUpdate | formatDate('MM-DD-YYYY, h:mm:ss a') }}
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-file :disabled="disableFields" v-model="codesInventoryCSV"
                            placeholder="Choose your CSV with the new codes to add to the inventory..."
                            drop-placeholder="Drop file here..." accept=".csv">
                        </b-form-file>
                        <a href="#" @click.prevent="downloadTemplate">Download Template</a>
                    </b-col>
                    <b-col cols="12">
                        <a href="#" @click.prevent="showErrors" style="color:red" v-if="errorsWereFound">
                            <i class="fas fa-exclamation-circle"></i>
                            <span> Errors were found, click here for details</span>
                        </a>
                    </b-col>
                </b-row>
            </div>
            <b-modal id="csv-errors" title="CSV Invalid values found" hide-footer>
                <span v-if="fatalCSVError">
                    <b style="color:red">General Error: </b> <span>{{ fatalCSVError }}</span><br>
                </span>
                <b v-if="csvErrors.length > 0">Error List</b>
                <ul>
                    <li v-for="r in csvErrors" :key="r">
                        {{ r }}
                    </li>
                </ul>
            </b-modal>

            <NewClearSaveButtons :titles="buttonTitles" :disable="disableFields" :disable-save="!isFormCompleted"
                :disable-clear="disableFields" :show-clear-modal="true" @new-clicked="createPlan"
                @clear-clicked="clearConfirmation" @save-clicked="savePlan" />

        </card>
    </div>
</template>

<script lang="ts">
import axios from 'axios';
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';

import SearchComponent from '@/components/SearchComponent.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import NotesList from '@/components/Note/NotesList.vue';
import NotesForm from '@/components/Note/NotesForm.vue';
import { RelationshipCode } from '@/models/PatientPlan';

import { Plan } from '@/models/Plan';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { PharmacyBenefitManager } from '@/models/PharmacyBenefitManager';
import { OrderAuditConfiguration } from "@/models/OrderAudit";
import { EntityType } from "@/models/EntityType";
import ProviderType = Plan.ProviderType;
import { Configuration } from "@/models/Configuration";


@Component({
    name: 'PlanDetailsPage',
    components: {
        SearchComponent,
        Checkbox,
        NewClearSaveButtons,
        EnumSelector,
        NotesList,
        NotesForm
    },
})
export default class PlanDetailsPage extends Mixins(DisableAutocompleteMixin) {
    @Prop({
        default: () => {
            return {
                save: "Save Changes",
                new: "New Plan",
                clear: "Clear",
            };
        }
    }) protected buttonTitles!: any;
    @Prop({ default: false }) private createNewPlan!: any;
    @Prop({ default: null }) private parsedPlanData!: Partial<Plan> | null;

    private pbm: PharmacyBenefitManager = new PharmacyBenefitManager();
    private plan: Plan = new Plan();
    private hasAppliedParsedData: boolean = false;
    protected orderAuditConfig: OrderAuditConfiguration = new OrderAuditConfiguration();
    protected providerTypeEnums = Plan.ProviderType;
    protected searchValue = "";
    protected displayName = "";
    private newMode = false;
    private isOffline: boolean = false;
    protected flagOptions = Plan.flagOptions;
    protected relationshipCodes = RelationshipCode;
    private codesInventoryCSV: File = new File([], "");
    private csvErrors: Array<string> = [];
    private fatalCSVError = "";

    private configuration: Configuration = new Configuration();

    created() {
        this.fetchConfiguration();
        if (this.createNewPlan) {
            this.createPlan();
            this.applyParsedPlanData();
        }
    }

    @Watch('parsedPlanData')
    onParsedPlanDataChanged() {
        if (!this.hasAppliedParsedData) {
            this.applyParsedPlanData();
        }
    }

    applyParsedPlanData() {
        if (!this.parsedPlanData || this.hasAppliedParsedData) return;

        // Pre-fill all available fields from AI-parsed data
        Object.assign(this.plan, this.parsedPlanData);
        this.hasAppliedParsedData = true;

        // Trigger BIN lookup for PBM if BIN is available
        if (this.parsedPlanData.bin) {
            this.binSet();
        }
    }

    fetchConfiguration() {
        axios.get<Configuration>("/api/Configuration")
            .then(response => {
                this.configuration = new Configuration(response.data);
            });
    }

    get disableFields(): boolean | null {
        return (this.plan?.id || 0) == 0 && !this.newMode;
    }

    get isFormCompleted() {
        const b1 = !this.disableFields && !!(this.plan.name && this.plan.bin);
        const validOfflineCopay = this.isOffline && (this.plan.offlineCopay || this.plan.offlineCopay == 0)
            || !this.isOffline && !this.plan.offlineCopay;

        return b1 && validOfflineCopay;
    }

    async binSet() {
        const config = { params: { bin: this.plan.bin } };
        const pbm = (await axios.get<PharmacyBenefitManager>("api/PharmacyBenefitManager/BIN", config)).data;
        this.pbm = new PharmacyBenefitManager(pbm);
    }

    clearConfirmation() {
        this.clearForm();
    }

    createPlan() {
        this.newMode = true;
        this.isOffline = false;
        this.plan = new Plan();
        this.pbm = new PharmacyBenefitManager();
        this.orderAuditConfig = new OrderAuditConfiguration();
    }

    clearForm() {
        this.newMode = false;
        this.isOffline = false;
        this.plan = new Plan();
        this.pbm = new PharmacyBenefitManager();
        this.orderAuditConfig = new OrderAuditConfiguration();
    }

    savePlan() {
        if (!this.configuration.planTypeCanBeUnknown && this.plan.providerType == ProviderType.Unknown) {
            this.$notification(NotificationOptions.error("Provider Type must not be Unknown."));
            return;
        }
        this.plan.offlineCopay = this.isOffline ? (this.plan.offlineCopay || 0) : null;
        if (this.pbm?.id as number) this.plan.pbmId = this.pbm.id as number;
        else this.plan.pbmId = null as unknown as number;
        axios.post<Plan>('/Plan', this.plan)
            .then(response => {
                if (response.data.id) {
                    this.plan = new Plan(response.data);
                    this.$notification(NotificationOptions.successSaveNotificationPreset("Plan"));
                    if (!this.codesInventoryCSV?.size) {
                        this.closeNewForm();
                    } else this.uploadCodes();
                    this.orderAuditConfig.entityType = EntityType.Plan;
                    this.orderAuditConfig.entityId = this.plan.id;
                    axios.post<OrderAuditConfiguration>("/api/OrderAuditConfig", this.orderAuditConfig)
                        .then(response => {
                            if (response.data.id) {
                                this.orderAuditConfig = new OrderAuditConfiguration(response.data);
                            }
                        })
                        .catch(err => this.$notification(NotificationOptions.error(err)));
                }
            })
            .catch(error => {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Plan", error));
            });

    }

    closeNewForm() {
        if (this.createNewPlan) {
            this.$emit("created", this.plan);
            this.plan = new Plan();
        }
    }

    onPlanChanged(value: Plan) {
        this.plan = value;
        this.isOffline = this.plan.offlineCopay != null;
        axios.get<OrderAuditConfiguration>(`/api/OrderAuditConfig/Plan/${this.plan.id}`)
            .then(configsResponse => {
                if (configsResponse.data) {
                    this.orderAuditConfig = new OrderAuditConfiguration(configsResponse.data);
                }
            })
        if (this.plan.pbmId) {
            axios.get<PharmacyBenefitManager>(`/PharmacyBenefitManager/${this.plan.pbmId}`)
                .then(pbmResponse => {
                    this.pbm = new PharmacyBenefitManager(pbmResponse.data);
                });
        }
    }

    uploadCodes() {
        if (!this.codesInventoryCSV?.size) return;

        const formData = new FormData();
        formData.append('csvFile', this.codesInventoryCSV);
        const errorIconMsg = 'Click error details for more information.'
        this.csvErrors = [];
        this.fatalCSVError = "";
        axios.post<{ codesCount: number, errors: any }>(`/plan/${this.plan.id}/codes-inventory/`, formData
            , { headers: { 'Content-Type': 'multipart/form-data' } }
        ).then(response => {
            this.csvErrors = response.data?.errors;
            if (response.data.codesCount === 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    'CSV processed, but no new plan codes were inserted. '
                    + (this.errorsWereFound ? errorIconMsg : "All introduced plan codes were already registered in the database before or file is empty.")
                    , NotificationOptions.NotificationTypes.warning));
            } else if (response.data?.codesCount > 0) {
                this.updateCodesAvailableCount();
                if (this.errorsWereFound)
                    this.$notification(NotificationOptions.notificationOptionsPreset(
                        `CSV processed, ${response.data.codesCount} new codes inserted, but some errors were found. ` + errorIconMsg
                        , NotificationOptions.NotificationTypes.warning
                    ));
                else
                    this.$notification(NotificationOptions.successSaveNotificationPreset("Plan Codes in CSV file"));
            }

            if (!this.errorsWereFound) {
                this.closeNewForm();
            }

        })
            .catch(err => {
                this.$notification(NotificationOptions.errorSaveNotificationPreset('Plan codes', err));
                this.fatalCSVError = `${err.message}: ${err.response?.data?.substring(0, 200)}...`;
                console.log(err, { err, response: err?.response });
            })
    }

    updateCodesAvailableCount() {
        this.$http.get<Plan>(`plan/${this.plan.id}`)
            .then(res => {
                this.plan.inventoryQuantityAvailable = res.data?.inventoryQuantityAvailable;
                this.plan.inventoryLastUpdate = res.data?.inventoryLastUpdate;
            }).catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
    }

    showErrors() {
        this.$bvModal.show('csv-errors');
    }

    downloadTemplate() {
        axios.get(`plan/codes-inventory-template`, { responseType: "blob" })
            .then(res => {
                const fileURL = URL.createObjectURL(res.data);
                //var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                const fileLink = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.setAttribute('download', 'InventoryCodesTemplate.csv');
                document.body.appendChild(fileLink);

                fileLink.click();
            })
            .catch(err => {
                console.error("Error while inventory codes template.", { err, response: err.response });
                this.$notification(NotificationOptions.error(err));
            })
    }

    get errorsWereFound(): boolean {
        return this.csvErrors?.length > 0 || this.fatalCSVError?.length > 0;
    }

    EntityType = EntityType;

    @Ref("notesList") protected notesList!: any;

    refreshNotes() {
        this.notesList?.loadNotes();
    }

} // close export of class
</script>

<style scoped></style>
